import type { PermisosObraBodega, PermisosSeccion } from '../constants/bodega-permisos';
import type { TipoProducto, Control, Producto, Pagina } from '../../modules/catalogo/modelos/catalogo.model';

// Tipos del módulo Bodega: espejo del contrato real del back (controllers y DTOs de
// PortalHexxa_Back/src/modules/bodega). Las cantidades viajan siempre como number y las fechas
// como ISO 8601 UTC. El catálogo (productos, categorías, prefijos y códigos) es de todo el holding; el
// inventario, el stock y los movimientos son de una obra.


/** Una obra donde el usuario tiene Bodega y lo que puede hacer EN ella (GET /bodega/mis-obras). */
export interface ObraConBodega {
  id: number;
  nombre: string;
  codigo: string | null;
  empresaId: number;
  permisos: PermisosObraBodega;
}

export interface MisObrasBodega {
  obras: ObraConBodega[];
  /**
   * Los bloques globales del Catálogo (calculados con las claves `catalogo_productos`, `catalogo_prefijos` y `catalogo_revision`). Bodega ya no
   * los usa: el módulo Catálogo lee sus permisos de la sesión (CatalogoPermisos).
   */
  catalogo?: PermisosSeccion;
  prefijos?: PermisosSeccion;
  revision?: PermisosSeccion;
}


// ── Inventario de la obra ────────────────────────────────────────────────────────────────
/**
 * Piezas de un producto por unidad en esta obra (sin contar las dadas de baja). `disponibles` es lo que
 * el inventario muestra como cantidad.
 */
export interface UnidadesResumen {
  disponibles: number;
  enPrestamo: number;
  enMantencion: number;
  total: number;
}

/** La empresa dueña de un stock o de una pieza. */
export interface PropietarioRef {
  id: number;
  nombre: string;
}

/** Una opción del filtro «Propietario» del inventario (GET /inventario → `propietarios`): la empresa propia va primero. */
export interface PropietarioFiltro extends PropietarioRef {
  propio: boolean;
}

/** Cuánto tiene un dueño de un producto en la bodega (GET /disponible → `propietarios`): el propio va primero. */
export interface PropietarioDisponible extends PropietarioFiltro {
  cantidad: number;
}

export interface InventarioItem {
  /** De quién es este stock. Ausente en un servidor anterior a la pata 2: es el stock propio de la obra. */
  propietario?: PropietarioRef;
  /** false = stock de OTRA empresa (lo que la Central compró o mandó): sin mínimo ni alertas, y no se edita desde la obra. Ausente = propio. */
  propio?: boolean;
  producto: Producto;
  /** En un producto por unidad: las piezas disponibles. */
  cantidad: number;
  /** null = sin mínimo: no hay alertas para este producto en esta obra. */
  stockMinimo: number | null;
  ubicacion: string | null;
  /** cantidad ≤ stockMinimo (con mínimo definido). Incluye a los agotados. */
  bajoMinimo: boolean;
  /** cantidad = 0 con mínimo definido. */
  agotado: boolean;
  /** El desglose de las piezas; null en un producto por cantidad. */
  unidades?: UnidadesResumen | null;
}

export interface FiltrosInventario {
  /** La empresa propia = solo lo propio; otra empresa = solo su stock dentro de esta bodega. */
  propietarioId?: number;
  q?: string;
  tipo?: TipoProducto;
  categoriaId?: number;
  soloAlertas?: boolean;
  page?: number;
  limit?: number;
}

/**
 * GET /inventario: `items` es lo PROPIO (paginado, como siempre); `ajenos` el stock de otras empresas en esta bodega (completo, con los mismos
 * filtros; sin alertas) y `propietarios` las opciones del filtro. Un servidor anterior a la pata 2 no trae las dos últimas.
 */
export interface PaginaInventario extends Pagina<InventarioItem> {
  ajenos?: InventarioItem[];
  propietarios?: PropietarioFiltro[];
}

/** Contadores de la tarjeta del hub y del menú. `alertas` = `bajoMinimo`. */
export interface ResumenInventario {
  productos: number;
  bajoMinimo: number;
  agotados: number;
  alertas: number;
  /** Préstamos abiertos cuya fecha de vencimiento ya pasó. */
  prestamosVencidos?: number;
  /** Préstamos abiertos que vencen hoy. */
  prestamosVencenHoy?: number;
}

/** GET /disponible: cantidad, mínimo y ubicación de un producto en la obra (0 si no tiene fila). */
export interface Disponible {
  productoId: number;
  cantidad: number;
  stockMinimo: number | null;
  ubicacion: string | null;
  /** Cómo se lleva la cuenta del producto, y en uno por unidad el desglose de sus piezas. */
  control?: Control;
  unidades?: UnidadesResumen | null;
  /** Quién tiene stock de este producto en la bodega y cuánto (el propio primero). Ausente en un servidor anterior: solo hay stock propio. */
  propietarios?: PropietarioDisponible[];
}

/** Estado de un producto en la obra tras un movimiento o un cambio de mínimo/ubicación. */
export interface EstadoStock extends Disponible {
  bajoMinimo: boolean;
  agotado: boolean;
}

/** Cuerpo de PATCH /stock/:productoId. Nunca lleva cantidad. null borra el valor. */
export interface CambioStock {
  stockMinimo?: number | null;
  ubicacion?: string | null;
}

// ── Movimientos ──────────────────────────────────────────────────────────────────────────
export type TipoMovimiento = 'entrada' | 'salida' | 'ajuste';
export type OrigenMovimiento = 'manual' | 'carga_inicial' | 'traspaso';

export type MotivoAjuste = 'llego_de_otra_obra' | 'merma' | 'devolucion' | 'correccion_conteo' | 'otro';

export const MOTIVOS_AJUSTE: { clave: MotivoAjuste; etiqueta: string }[] = [
  { clave: 'llego_de_otra_obra', etiqueta: 'Llegó de otra obra' },
  { clave: 'merma',              etiqueta: 'Merma' },
  { clave: 'devolucion',         etiqueta: 'Devolución' },
  { clave: 'correccion_conteo',  etiqueta: 'Corrección de conteo' },
  { clave: 'otro',               etiqueta: 'Otro' },
];

export function etiquetaMotivo(clave: string | null | undefined): string {
  if (!clave) return '';
  return MOTIVOS_AJUSTE.find(m => m.clave === clave)?.etiqueta ?? clave;
}

export const ETIQUETA_TIPO_MOVIMIENTO: Record<TipoMovimiento, string> = {
  entrada: 'Ingreso',
  salida: 'Salida',
  ajuste: 'Ajuste',
};

export interface LineaMovimiento {
  productoId: number;
  nombre: string;
  unidad: string;
  /** Con signo: positivo suma, negativo resta. */
  delta: number;
  saldoPosterior: number;
  ubicacion: string | null;
}

export interface Movimiento {
  id: number;
  obraId: number;
  tipo: TipoMovimiento;
  creadoEn: string;
  usuario: { id: number; nombre: string };
  receptor: string | null;
  descripcion: string | null;
  documento: string | null;
  motivo: string | null;
  origen: OrigenMovimiento;
  lineas: LineaMovimiento[];
}

export interface LineaCarrito {
  productoId: number;
  /** Entrada y salida: mayor que 0. Ajuste: distinta de 0 y con signo. */
  cantidad: number;
  /** Solo en entradas. Si no viene, el producto conserva su última ubicación. */
  ubicacion?: string;
}

/** Cuerpo de POST /movimientos: un carrito completo = una petición = una transacción. */
export interface MovimientoNuevo {
  tipo: TipoMovimiento;
  /** UUID del carrito (8 a 80 caracteres): el reintento de un carrito no lo duplica. */
  claveCliente: string;
  lineas: LineaCarrito[];
  /** Solo en entradas. */
  documento?: string;
  /** Obligatorio y solo en salidas: texto libre. */
  receptor?: string;
  descripcion?: string;
  /** Obligatorio y solo en ajustes. Con «otro», la descripción es obligatoria. */
  motivo?: MotivoAjuste;
  /** Solo en ajustes: la empresa dueña del stock que se ajusta (omitir = el stock propio de la obra). Ajustar el de otra empresa es de la Central (403). */
  propietarioId?: number;
}

export interface RespuestaMovimiento {
  movimiento: Movimiento;
  saldos: EstadoStock[];
  /** true = la claveCliente ya se había guardado y el servidor devolvió el mismo movimiento. */
  repetido: boolean;
}

export interface UsuarioMovimiento {
  id: number;
  nombre: string;
}

/** Una línea de GET /productos/:id/historial. */
export interface LineaHistorial {
  movimientoId: number;
  creadoEn: string;
  tipo: TipoMovimiento;
  usuario: UsuarioMovimiento;
  receptor: string | null;
  descripcion: string | null;
  documento: string | null;
  motivo: string | null;
  delta: number;
  saldoPosterior: number;
  ubicacion: string | null;
}

// ── Unidades de herramienta ──────────────────────────────────────────────────────────────
/**
 * Estado de una pieza. `entregada` se muestra como «prestada» y solo ocurre por un préstamo; `baja` es terminal
 * salvo una dada de baja por extravío, que se puede reactivar si aparece.
 */
export type EstadoUnidad = 'disponible' | 'entregada' | 'en_mantencion' | 'baja' | 'en_transito';
export type MotivoBaja = 'danada' | 'extraviada' | 'otro';
export type TipoPrestamo = 'temporal_diario' | 'temporal_semanal' | 'indefinido';
export type EstadoVencimiento = 'vencido' | 'vence_hoy' | 'vigente';

export const ESTADOS_UNIDAD: EstadoUnidad[] = ['disponible', 'entregada', 'en_mantencion', 'baja', 'en_transito'];

/** Cómo se llama cada estado en pantalla. */
export const ETIQUETA_ESTADO_UNIDAD: Record<EstadoUnidad, string> = {
  disponible: 'Disponible',
  entregada: 'Prestada',
  en_mantencion: 'En mantención',
  baja: 'Dada de baja',
  en_transito: 'En tránsito',
};

export const MOTIVOS_BAJA: { clave: MotivoBaja; etiqueta: string }[] = [
  { clave: 'danada',     etiqueta: 'Dañada' },
  { clave: 'extraviada', etiqueta: 'Extraviada' },
  { clave: 'otro',       etiqueta: 'Otro motivo' },
];

export function etiquetaMotivoBaja(clave: string | null | undefined): string {
  if (!clave) return '';
  return MOTIVOS_BAJA.find(m => m.clave === clave)?.etiqueta ?? clave;
}

/** El producto de una pieza, con lo mínimo para mostrarla y etiquetarla. */
export interface ProductoDeUnidad {
  id: number;
  nombre: string;
  marca: string | null;
  unidad: string;
  tieneFoto: boolean;
  fotoVersion: number | null;
  categoria: { id: number; nombre: string };
}

/** El préstamo en que está una pieza que anda prestada. */
export interface PrestamoDeUnidad {
  id: number;
  receptor: string;
  tipo: TipoPrestamo;
  /** AAAA-MM-DD del día en Chile. */
  fechaEntrega: string;
  fechaVencimiento: string;
  estadoVencimiento: EstadoVencimiento;
  diasDeAtraso: number;
}

export interface Unidad {
  id: number;
  codigo: string;
  estado: EstadoUnidad;
  ubicacion: string | null;
  motivoBaja: MotivoBaja | null;
  notaBaja: string | null;
  producto: ProductoDeUnidad;
  /** Solo si está prestada. */
  prestamo: PrestamoDeUnidad | null;
  creadoEn: string;
  actualizadoEn: string;
  /** La empresa dueña de la pieza (la Central, por ejemplo, si la mandó a la obra); null o ausente = la de la obra. */
  propietario?: PropietarioRef | null;
  /** Cuándo entró a esta bodega (la recepción de un traspaso); null o ausente si siempre estuvo en ella. */
  recibidaEnBodegaEn?: string | null;
}

/** Cuerpo de POST /unidades: o `cantidad` (el sistema genera los códigos) o `codigos` (piezas ya etiquetadas). */
export interface AltaUnidades {
  productoId: number;
  /** De 1 a 200. */
  cantidad?: number;
  /** De 1 a 200: empiezan con letra, solo letras y números, de 3 a 30 caracteres. */
  codigos?: string[];
  ubicacion?: string;
  /** UUID del alta: un reintento devuelve las mismas unidades en vez de repetirlas. */
  claveCliente: string;
}

export interface LoteAlta {
  id: number;
  cantidad: number;
  ubicacion: string | null;
  codigosExplicitos: boolean;
  creadoEn: string;
}


export interface RespuestaAlta {
  lote: LoteAlta;
  /** El producto y sus unidades, con el código listo para etiquetar. */
  producto: ProductoDeUnidad;
  unidades: Unidad[];
  /** true = la claveCliente ya se había guardado y el servidor devolvió las mismas unidades. */
  repetido: boolean;
}

/** Filtros de GET /unidades. Sin `estado` ni `incluirBajas` no trae las dadas de baja. */
export interface FiltrosUnidades {
  productoId?: number;
  estado?: EstadoUnidad;
  incluirBajas?: boolean;
  /** Busca en el código, la ubicación y el nombre o la marca del producto. */
  q?: string;
  page?: number;
  /** Hasta 500 (50 si no se dice). */
  limit?: number;
}

/** Tope de piezas por página que acepta el back. */
export const MAX_UNIDADES_POR_PAGINA = 500;

/** Qué cambia en la historia de una herramienta: altas, préstamos, mantenciones, bajas, ubicación y aplazamientos. */
export type TipoEventoHerramienta =
  | 'alta' | 'entrega' | 'devolucion' | 'mantencion' | 'reactivacion' | 'baja' | 'ubicacion' | 'aplazamiento' | 'traspaso';

/** Una fila del historial de herramientas (del de una pieza y del de la obra). */
export interface EventoHerramienta {
  id: number;
  tipo: TipoEventoHerramienta;
  creadoEn: string;
  usuario: { id: number; nombre: string };
  productoId: number | null;
  unidadId: number | null;
  prestamoId: number | null;
  estadoAnterior: EstadoUnidad | null;
  estadoNuevo: EstadoUnidad | null;
  nota: string | null;
  cantidad: number | null;
  /** La ubicación antes y después; en una baja, `valorNuevo` es el motivo; en una reactivación, `valorAnterior` es el de la baja. */
  valorAnterior: string | null;
  valorNuevo: string | null;
  /** Solo aplazamientos (AAAA-MM-DD). */
  fechaAnterior: string | null;
  fechaNueva: string | null;
}

/** GET /unidades/por-codigo/:codigo. `historial` viene solo si el perfil tiene el permiso de historial EN esa obra. */
export interface DetalleUnidad {
  unidad: Unidad;
  historial: EventoHerramienta[] | null;
}

/** Cuerpo de POST /unidades/:id/estado. «Prestada» no se pide: solo ocurre al entregar en un préstamo. */
export interface CambioEstadoUnidad {
  estado: 'disponible' | 'en_mantencion' | 'baja';
  /** Solo al dar de baja. */
  motivoBaja?: MotivoBaja;
  /** Con la baja «otro» y al reactivar una extraviada es obligatoria; en lo demás es opcional. */
  nota?: string;
}

// ── Préstamos ────────────────────────────────────────────────────────────────────────────
export const ETIQUETA_TIPO_PRESTAMO: Record<TipoPrestamo, string> = {
  temporal_diario: 'Del día',
  temporal_semanal: 'De la semana',
  indefinido: 'Indefinido',
};

/** Cómo volvió una herramienta. */
export type ResultadoDevolucion = 'buen_estado' | 'con_dano' | 'no_volvio';
export type EstadoPrestamo = 'abierto' | 'cerrado';

/** Una línea de un préstamo: una pieza concreta (con su código) o un producto por cantidad. */
export interface LineaPrestamo {
  id: number;
  productoId: number;
  nombre: string;
  /** Unidad de medida del producto (und, m…). */
  medida: string;
  tipo: 'unidad' | 'cantidad';
  unidad: { id: number; codigo: string; estado: EstadoUnidad } | null;
  cantidad: number;
  devuelta: number;
  perdida: number;
  pendiente: number;
  resuelta: boolean;
  /** Solo líneas por unidad. */
  resultado: ResultadoDevolucion | null;
  resueltaEn: string | null;
}

export interface Prestamo {
  id: number;
  obraId: number;
  receptor: string;
  descripcion: string | null;
  tipo: TipoPrestamo;
  estado: EstadoPrestamo;
  /** AAAA-MM-DD, día en Chile. */
  fechaEntrega: string;
  fechaVencimiento: string;
  /** La que se calculó al entregar: la de arriba cambia con cada aplazamiento. */
  fechaVencimientoInicial: string;
  /** Solo de un préstamo abierto. */
  estadoVencimiento: EstadoVencimiento | null;
  diasDeAtraso: number;
  cerradoEn: string | null;
  usuario: { id: number; nombre: string };
  creadoEn: string;
  lineas: LineaPrestamo[];
  resumen: { lineas: number; abiertas: number };
}

export interface DevolucionDePrestamo {
  id: number;
  creadoEn: string;
  usuario: { id: number; nombre: string };
  lineas: { lineaId: number; productoId: number; nombre: string; codigo: string | null; resultado: ResultadoDevolucion; cantidad: number; nota: string | null }[];
}

export interface PrestamoDetalle extends Prestamo {
  devoluciones: DevolucionDePrestamo[];
  aplazamientos: EventoHerramienta[];
}

/** Una línea del cuerpo de POST /prestamos: una pieza por su código, o un producto por cantidad. Nunca las dos cosas. */
export interface LineaEntregaNueva {
  codigo?: string;
  productoId?: number;
  cantidad?: number;
}

/** Cuerpo de POST /prestamos. */
export interface EntregaNueva {
  receptor: string;
  descripcion?: string;
  tipo: TipoPrestamo;
  /** Solo (y obligatoria) en un préstamo indefinido: AAAA-MM-DD, desde hoy en adelante. */
  fechaEstimada?: string;
  lineas: LineaEntregaNueva[];
  /** UUID de la entrega: un reintento devuelve el mismo préstamo en vez de repetirlo. */
  claveCliente: string;
}

export interface RespuestaEntrega {
  prestamo: PrestamoDetalle;
  /** Cómo quedó el stock de los productos por cantidad que se entregaron. */
  saldos: EstadoStock[];
  /** true = la claveCliente ya se había guardado y el servidor devolvió el mismo préstamo. */
  repetido: boolean;
}

/** Una herramienta por unidad que vuelve, identificada solo por su código (se escanea). */
export interface UnidadDevuelta {
  codigo: string;
  resultado: ResultadoDevolucion;
  /** «con daño» y «no volvió»: qué pasó (opcional). */
  nota?: string;
}

/** Cuerpo de POST /prestamos/devolver-por-codigo: el sistema encuentra el préstamo de cada pieza. */
export interface DevolucionPorCodigo {
  unidades: UnidadDevuelta[];
  /** UUID de la devolución: un reintento devuelve lo mismo en vez de repetirlo. */
  claveCliente: string;
}

/** Una línea de un préstamo que se devuelve (o se da por no devuelta). la cantidad solo va en líneas por cantidad. */
export interface LineaDevuelta {
  lineaId: number;
  resultado: ResultadoDevolucion;
  cantidad?: number;
  nota?: string;
}

/** Cuerpo de POST /prestamos/:id/devoluciones. */
export interface DevolucionDeLineas {
  lineas: LineaDevuelta[];
  claveCliente: string;
}

export interface RespuestaDevolucion {
  devolucion: { id: number; creadoEn: string; prestamoId: number | null };
  /** Los préstamos que esta devolución tocó, ya con su estado nuevo. */
  prestamos: PrestamoDetalle[];
  /** Cómo quedó el stock de los productos por cantidad que volvieron en buen estado. */
  saldos: EstadoStock[];
  /** true = la claveCliente ya se había guardado y el servidor devolvió lo mismo. */
  repetido: boolean;
}

/** Qué préstamos se listan: con herramientas por devolver, de esos los que ya vencieron, o los ya resueltos del todo. */
export type FiltroEstadoPrestamo = 'abiertos' | 'vencidos' | 'cerrados';

/** Filtros de GET /prestamos. Las fechas son de ENTREGA (AAAA-MM-DD, inclusivas). */
export interface FiltrosPrestamos {
  estado?: FiltroEstadoPrestamo;
  receptor?: string;
  desde?: string;
  hasta?: string;
  page?: number;
  /** Hasta 100. */
  limit?: number;
}

/** Filtros de GET /prestamos/prestado. */
export interface FiltrosPrestado {
  productoId?: number;
  receptor?: string;
  page?: number;
  limit?: number;
}

/** Una herramienta que está prestada ahora (una fila de GET /prestamos/prestado): qué, a quién, desde cuándo y hasta cuándo. */
export interface LineaPrestada {
  lineaId: number;
  prestamoId: number;
  receptor: string;
  tipoPrestamo: TipoPrestamo;
  fechaEntrega: string;
  fechaVencimiento: string;
  estadoVencimiento: EstadoVencimiento;
  diasDeAtraso: number;
  producto: { id: number; nombre: string; medida: string };
  tipo: 'unidad' | 'cantidad';
  unidad: { id: number; codigo: string } | null;
  /** Cuántas unidades faltan por devolver (1 en una pieza). */
  pendiente: number;
}

/** GET /prestamos/alertas: los préstamos abiertos que ya vencieron o vencen hoy, el más atrasado primero. */
export interface RespuestaAlertas {
  items: Prestamo[];
  /** Todos los que hay (no solo los de esta página). */
  total: number;
  vencidos: number;
  vencenHoy: number;
  page: number;
  limit: number;
}

/** Cuerpo de POST /prestamos/:id/aplazar. */
export interface Aplazamiento {
  /** AAAA-MM-DD: desde hoy en adelante y distinta de la fecha vigente. */
  fechaNueva: string;
  motivo?: string;
}

/** GET /unidades/resolver: las piezas que existen en esta obra (con su estado) y los códigos que no. */
export interface ResolucionUnidades {
  items: Unidad[];
  noEncontrados: string[];
}

/** Una pieza que el servidor no pudo entregar porque no estaba disponible. */
export interface UnidadNoDisponible {
  codigo: string;
  estado: EstadoUnidad;
  estadoEtiqueta: string;
  producto: string;
}


// ── Errores tipados de la API ────────────────────────────────────────────────────────────
/** Falla de una llamada a Bodega con el mensaje ya en español y listo para mostrar. */
export class ErrorBodega extends Error {
  /**
   * `incierto` = la petición pudo llegar al servidor y guardarse aunque la respuesta no volvió
   * (corte de red, error 5xx). Quien registra un movimiento no debe editar el carrito antes de reintentar.
   */
  constructor(readonly status: number, mensaje: string, readonly incierto = false, readonly code?: string) {
    super(mensaje);
    this.name = 'ErrorBodega';
  }
}

/**
 * Los `code` del servidor que las pantallas de la etapa 2 reconocen para ubicar el mensaje junto al campo que corresponde (están en `ErrorBodega.code`).
 * Todos traen su mensaje en español: sin reconocer el código, mostrar el mensaje basta.
 */
export const CODIGOS_ERROR_ETAPA_2 = [
  'PREFIJO_REQUERIDO', 'CONSUMIBLE_SIN_PREFIJO', 'PREFIJO_NO_EXISTE', 'PREFIJO_INACTIVO', 'PRODUCTO_SIN_PREFIJO', 'PREFIJO_NO_SE_CAMBIA',
  'PREFIJO_REPETIDO', 'PREFIJO_EN_USO', 'PREFIJO_CON_PIEZAS', 'PREFIJO_NO_REGISTRADO',
  'CATEGORIA_NOMBRE_REPETIDO', 'CATEGORIA_CICLO', 'CATEGORIA_CON_CONTENIDO',
  'ELIMINACION_NO_PERMITIDA', 'CONSUMIBLE_CON_EAN_REAL',
] as const;
export type CodigoErrorEtapa2 = (typeof CODIGOS_ERROR_ETAPA_2)[number];

/** Una línea del carrito que pide más de lo que hay en la bodega. */
export interface LineaSinStock {
  productoId: number;
  nombre: string;
  disponible: number;
  solicitado: number;
}

/** 409 de POST /movimientos: ninguna línea se guardó. `lineas` dice cuáles faltan y cuánto. */
export class ErrorStockInsuficiente extends ErrorBodega {
  constructor(mensaje: string, readonly lineas: LineaSinStock[]) {
    super(409, mensaje);
    this.name = 'ErrorStockInsuficiente';
  }
}

/** 409 cuando la claveCliente del carrito ya se usó con otro contenido: el movimiento anterior pudo guardarse. */
export class ErrorClaveUsada extends ErrorBodega {
  constructor(mensaje: string) {
    super(409, mensaje);
    this.name = 'ErrorClaveUsada';
  }
}

/** 409 al registrar un código que ya pertenece a otro producto (activo o desactivado). */
export class ErrorCodigoRepetido extends ErrorBodega {
  constructor(
    mensaje: string,
    readonly codigo: string,
    readonly productoId: number,
    readonly productoNombre: string,
  ) {
    super(409, mensaje);
    this.name = 'ErrorCodigoRepetido';
  }
}

/**
 * 409 al cambiar el control de un producto (por unidad ↔ por cantidad) cuando ya hay algo que contar de la
 * otra manera. Trae los números para decir exactamente qué lo impide.
 */
export class ErrorControlBloqueado extends ErrorBodega {
  constructor(
    mensaje: string,
    /** Obras en las que el producto tiene stock. */
    readonly obrasConStock: number,
    /** Unidades registradas (también las dadas de baja). */
    readonly unidades: number,
    /** Préstamos por cantidad todavía abiertos. */
    readonly prestamosAbiertos: number,
  ) {
    super(409, mensaje);
    this.name = 'ErrorControlBloqueado';
  }
}

/**
 * 409 al eliminar una categoría que todavía tiene contenido: `hijos` y `productos` son los directos y `impide` trae los textos listos para
 * mostrar («Tiene 2 subcategorías», «Tiene 5 productos»).
 */
export class ErrorCategoriaConContenido extends ErrorBodega {
  constructor(mensaje: string, readonly hijos: number, readonly productos: number, readonly impide: string[]) {
    super(409, mensaje, false, 'CATEGORIA_CON_CONTENIDO');
    this.name = 'ErrorCategoriaConContenido';
  }
}

/**
 * 409 al eliminar un id creado por error (producto, pieza o código generado) que ya tiene historia: `impedimentos` dice qué lo impide y
 * `sugerencia` qué hacer en su lugar (editar, por ejemplo).
 */
export class ErrorEliminacionNoPermitida extends ErrorBodega {
  constructor(mensaje: string, readonly impedimentos: string[], readonly sugerencia: string | null) {
    super(409, mensaje, false, 'ELIMINACION_NO_PERMITIDA');
    this.name = 'ErrorEliminacionNoPermitida';
  }
}

/** 409 al registrar piezas ya etiquetadas cuyos códigos ya existen (en esta obra o en otra). */
export class ErrorCodigosRegistrados extends ErrorBodega {
  constructor(mensaje: string, readonly codigos: string[]) {
    super(409, mensaje);
    this.name = 'ErrorCodigosRegistrados';
  }
}

/** 409 de POST /prestamos: ninguna pieza salió, porque estas no estaban disponibles (el servidor dice por qué). */
export class ErrorUnidadesNoDisponibles extends ErrorBodega {
  constructor(mensaje: string, readonly unidades: UnidadNoDisponible[]) {
    super(409, mensaje);
    this.name = 'ErrorUnidadesNoDisponibles';
  }
}

/** 400 de una salida con herramientas: las herramientas no salen por Salida, se entregan en préstamo (nada se guardó). */
export class ErrorHerramientaEnSalida extends ErrorBodega {
  constructor(mensaje: string, readonly productos: { productoId: number; nombre: string }[]) {
    super(400, mensaje);
    this.name = 'ErrorHerramientaEnSalida';
  }
}

/** 409 de una devolución: esa herramienta ya se devolvió o no figura como prestada (nada se guardó). */
export class ErrorHerramientaNoPrestada extends ErrorBodega {
  constructor(mensaje: string, readonly codigo: string | null, readonly lineaId: number) {
    super(409, mensaje);
    this.name = 'ErrorHerramientaNoPrestada';
  }
}

/** 404 de entregas y devoluciones por código: estos códigos no existen en esta obra. */
export class ErrorCodigosInexistentes extends ErrorBodega {
  constructor(mensaje: string, readonly codigos: string[]) {
    super(404, mensaje);
    this.name = 'ErrorCodigosInexistentes';
  }
}


// ── Días de bodega (apertura y cierre diario por obra) ───────────────────────────────────────────

export type EstadoDia = 'abierto' | 'cerrado';
/** Cómo está la obra ahora: sin día abierto, con el de hoy abierto o con uno anterior que quedó abierto. */
export type EstadoActualDia = 'sin_abrir' | 'abierto' | 'abierto_anterior';

export interface UsuarioDia {
  id: number;
  nombre: string;
}

/** Lo que falta devolver de un préstamo abierto (lo que el servidor cuenta como pendiente al abrir o cerrar un día). */
export interface PrestamoPendiente {
  prestamoId: number;
  receptor: string;
  descripcion: string | null;
  tipo: TipoPrestamo;
  fechaEntrega: string;
  fechaVencimiento: string;
  estadoVencimiento: EstadoVencimiento;
  diasDeAtraso: number;
  lineasPendientes: { lineaId: number; productoId: number; nombre: string; codigo: string | null; pendiente: number }[];
}

export interface AlertaDeStock {
  productoId: number;
  nombre: string;
  unidad: string;
  cantidad: number;
  stockMinimo: number | null;
  agotado: boolean;
}

/** La foto de lo pendiente al abrir (informativa: no bloquea). */
export interface PendientesApertura {
  fecha: string;
  prestamosVencidos: { total: number; items: PrestamoPendiente[] };
  alertasStock: { bajoMinimo: number; agotados: number; items: AlertaDeStock[] };
  /** Hay algo que revisar: la apertura exige confirmarlo. */
  hayPendientes: boolean;
  confirmado: boolean;
}

/** Lo que bloquea (y lo que solo informa) al cerrar el día. */
export interface PendientesCierre {
  fecha: string;
  bloqueantes: { total: number; items: PrestamoPendiente[] };
  indefinidosVencidos: { total: number; items: PrestamoPendiente[] };
  puedeCerrar: boolean;
}

export interface ResumenDia {
  fecha: string;
  desde: string;
  hasta: string;
  movimientos: {
    entradas: { movimientos: number; lineas: number };
    salidas: { movimientos: number; lineas: number };
    ajustes: { movimientos: number; lineas: number };
  };
  prestamos: { entregados: number; devoluciones: number; lineasDevueltas: number; aplazados: number };
  piezas: { aMantencion: number; aBaja: number };
  /** Cuántas operaciones de cada tipo hubo en el día (una llegada, una devolución o una salida cuenta una). Ausente en un servidor anterior. */
  operaciones?: { llegadasStock: number; devoluciones: number; salidas: number };
  alertas: {
    stockBajoMinimo: number;
    stockAgotados: number;
    prestamosAbiertos: number;
    prestamosVencidos: number;
    prestamosVencenHoy: number;
    indefinidosVencidos: number;
  };
}

export interface DiaBodega {
  id: number;
  obraId: number;
  /** AAAA-MM-DD, día de calendario en Chile. */
  fecha: string;
  estado: EstadoDia;
  abiertoPor: UsuarioDia;
  abiertoEn: string;
  cerradoPor: UsuarioDia | null;
  cerradoEn: string | null;
  nota: string | null;
  reaperturas: number;
  pendientesApertura: PendientesApertura | null;
  resumenCierre: ResumenDia | null;
}

export interface UltimoCierre {
  fecha: string;
  cerradoPor: UsuarioDia | null;
  cerradoEn: string | null;
  nota: string | null;
  resumen: ResumenDia | null;
}

/** GET /dia. */
export interface EstadoDelDia {
  hoy: string;
  estado: EstadoActualDia;
  puedeOperar: boolean;
  dia: DiaBodega | null;
  diaCerradoHoy: DiaBodega | null;
  ultimoCierre: UltimoCierre | null;
  pendientesApertura: PendientesApertura;
  pendientesCierre: PendientesCierre;
  /** Lo hecho hasta ahora en el día abierto (el mismo resumen que se guarda al cerrar); null sin día abierto. */
  resumenActual?: ResumenDia | null;
}

export interface RespuestaDia {
  dia: DiaBodega;
  /** true = ya estaba así (reintento o estado ya alcanzado): no se hizo nada nuevo. */
  repetido: boolean;
}

export interface AperturaNueva {
  claveCliente: string;
  confirmaPendientes?: boolean;
}

export interface CierreNuevo {
  claveCliente: string;
  nota?: string;
}

export interface ReaperturaNueva {
  motivo: string;
  claveCliente?: string;
}

/** Los códigos con que el servidor explica un 409 del día. */
export type CodigoDia =
  | 'DIA_CERRADO' | 'DIA_ANTERIOR_ABIERTO' | 'DIA_YA_CERRADO' | 'PENDIENTES_SIN_CONFIRMAR' | 'CIERRE_BLOQUEADO'
  | 'SIN_DIA_ABIERTO' | 'SIN_DIA_CERRADO_HOY' | 'HAY_OTRO_DIA_ABIERTO' | 'CLAVE_USADA' | 'CONFLICTO_SIMULTANEO';

/**
 * 409 del día: operar sin día abierto (DIA_CERRADO), con uno de una fecha anterior abierto (DIA_ANTERIOR_ABIERTO), abrir uno ya
 * cerrado, cerrar con pendientes… Nada se guardó. El mensaje ya viene en lenguaje simple.
 */
export class ErrorDia extends ErrorBodega {
  constructor(mensaje: string, readonly codigo: CodigoDia, readonly detalle: { fechaDia?: string; hoy?: string; fecha?: string } = {}) {
    super(409, mensaje);
    this.name = 'ErrorDia';
  }

  /** El error de una operación que se topó con el día cerrado o anterior: pide mirar de nuevo el estado del día. */
  get esDeOperacion(): boolean {
    return this.codigo === 'DIA_CERRADO' || this.codigo === 'DIA_ANTERIOR_ABIERTO';
  }
}

/** 409 al cerrar: estos préstamos temporales vencen hoy o antes y hay que resolverlos primero. */
export class ErrorCierreBloqueado extends ErrorDia {
  constructor(mensaje: string, readonly prestamos: PrestamoPendiente[], readonly total: number, fecha?: string) {
    super(mensaje, 'CIERRE_BLOQUEADO', { fecha });
    this.name = 'ErrorCierreBloqueado';
  }
}

/** 409 al abrir: hay pendientes que revisar y no se confirmaron. */
export class ErrorPendientesSinConfirmar extends ErrorDia {
  constructor(mensaje: string, readonly pendientes: PendientesApertura) {
    super(mensaje, 'PENDIENTES_SIN_CONFIRMAR');
    this.name = 'ErrorPendientesSinConfirmar';
  }
}
